/**
 * Walk an HTML5 drag-and-drop `DataTransferItem` list, expanding any
 * dropped folders into their nested file/folder contents.
 *
 * IMPORTANT: callers MUST capture the `DataTransferItem` objects
 * SYNCHRONOUSLY in the drop handler — `DataTransferItem` references and
 * the `webkitGetAsEntry()` return value are invalidated as soon as the
 * handler yields (await). Pass an already-snapshotted array to this
 * helper.
 *
 * Returns:
 * - `folders`: every directory path encountered, in shallow-to-deep
 *   order (each ends with `/`). Empty folders are included so the
 *   workspace mirrors the dropped structure visually.
 * - `files`: every leaf file with its full relative path (folder
 *   prefix included, e.g. `MyDocs/sub/foo.txt`).
 * - `looseFiles`: files dropped without an enclosing folder (top-level
 *   files in the same drop). Kept separate so the caller can route
 *   them through the flat-upload path if desired.
 */

const HIDDEN_NAME_PATTERNS = [/^\.DS_Store$/i, /^Thumbs\.db$/i, /^\._/];

function isHidden(name: string): boolean {
  return HIDDEN_NAME_PATTERNS.some((re) => re.test(name));
}

export interface FolderUploadTree {
  folders: string[];
  files: { file: File; relativePath: string }[];
  looseFiles: File[];
}

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
}
interface FileSystemFileEntryLike extends FileSystemEntryLike {
  isFile: true;
  file(success: (file: File) => void, error?: (err: unknown) => void): void;
}
interface FileSystemDirectoryEntryLike extends FileSystemEntryLike {
  isDirectory: true;
  createReader(): FileSystemDirectoryReaderLike;
}
interface FileSystemDirectoryReaderLike {
  readEntries(
    success: (entries: FileSystemEntryLike[]) => void,
    error?: (err: unknown) => void,
  ): void;
}

function readEntriesAll(
  reader: FileSystemDirectoryReaderLike,
): Promise<FileSystemEntryLike[]> {
  // `readEntries` returns entries in batches (browsers commonly cap at
  // ~100 per call). Loop until it returns an empty array.
  return new Promise((resolve, reject) => {
    const all: FileSystemEntryLike[] = [];
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all);
          return;
        }
        for (const entry of batch) all.push(entry);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}

function entryAsFile(entry: FileSystemFileEntryLike): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

async function walkEntry(
  entry: FileSystemEntryLike,
  parentRelativePath: string,
  out: FolderUploadTree,
): Promise<void> {
  if (isHidden(entry.name)) return;

  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntryLike;
    try {
      const file = await entryAsFile(fileEntry);
      const relativePath = `${parentRelativePath}${file.name}`;
      out.files.push({ file, relativePath });
    } catch {
      // Permissions / IO errors on a single entry shouldn't kill the
      // whole walk — surface as a missing file in the tree.
    }
    return;
  }

  if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntryLike;
    const dirRelativePath = `${parentRelativePath}${dirEntry.name}/`;
    out.folders.push(dirRelativePath);
    try {
      const entries = await readEntriesAll(dirEntry.createReader());
      for (const child of entries) {
        await walkEntry(child, dirRelativePath, out);
      }
    } catch {
      // Same rationale as file errors above.
    }
  }
}

interface DataTransferItemLike {
  kind: string;
  webkitGetAsEntry?: () => FileSystemEntryLike | null;
  getAsFile?: () => File | null;
}

/**
 * Whether a synchronously-captured DataTransferItem list contains any
 * directory entries. Cheap to call in the drop handler (no async work,
 * no entry traversal) so callers can decide whether to take the
 * tree-walk path or fall back to the flat `dataTransfer.files` path.
 */
export function dataTransferItemsHaveDirectory(
  items: DataTransferItemLike[],
): boolean {
  for (const item of items) {
    if (item.kind !== "file") continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry?.isDirectory) return true;
  }
  return false;
}

interface FileSystemHandleLike {
  kind: "file" | "directory";
  name: string;
}
interface FileSystemFileHandleLike extends FileSystemHandleLike {
  kind: "file";
  getFile(): Promise<File>;
}
export interface FileSystemDirectoryHandleLike extends FileSystemHandleLike {
  kind: "directory";
  values(): AsyncIterable<FileSystemHandleLike>;
}

async function walkDirectoryHandle(
  dir: FileSystemDirectoryHandleLike,
  prefix: string,
  out: { file: File; relativePath: string }[],
  dirs?: string[],
): Promise<void> {
  for await (const child of dir.values()) {
    if (isHidden(child.name)) continue;
    if (child.kind === "file") {
      try {
        const file = await (child as FileSystemFileHandleLike).getFile();
        out.push({ file, relativePath: `${prefix}${child.name}` });
      } catch {
        // Same rationale as drop-entry errors: one unreadable file
        // shouldn't kill the walk.
      }
    } else {
      const childPrefix = `${prefix}${child.name}/`;
      dirs?.push(childPrefix);
      await walkDirectoryHandle(
        child as FileSystemDirectoryHandleLike,
        childPrefix,
        out,
        dirs,
      );
    }
  }
}

/**
 * Recursively read a File System Access API directory handle (from
 * `window.showDirectoryPicker()`) into the same `{ file, relativePath }`
 * shape as the drop-tree walk. Paths are prefixed with the picked
 * folder's name, matching how `webkitRelativePath` reports them.
 */
export async function traverseDirectoryHandle(
  dir: FileSystemDirectoryHandleLike,
): Promise<{ file: File; relativePath: string }[]> {
  const out: { file: File; relativePath: string }[] = [];
  await walkDirectoryHandle(dir, `${dir.name}/`, out);
  return out;
}

/**
 * The same walk as `traverseDirectoryHandle`, keeping the directories it
 * passes through. A directory handle's `values()` yields subdirectories
 * whether or not they hold files, so empty subfolders survive here — only
 * the `webkitdirectory` fallback, a flat FileList, cannot express them.
 *
 * The picked root leads `folders`: the walk starts inside it, so nothing
 * else records it. `isHidden` does not apply to it either — the user picked
 * it explicitly.
 */
export async function traverseDirectoryHandleTree(
  dir: FileSystemDirectoryHandleLike,
): Promise<FolderUploadTree> {
  const files: { file: File; relativePath: string }[] = [];
  const folders = [`${dir.name}/`];
  await walkDirectoryHandle(dir, `${dir.name}/`, files, folders);
  return { folders, files, looseFiles: [] };
}

/**
 * Fold a `webkitdirectory` input's flat FileList into the same tree shape
 * the drop walk produces, so it reaches `enqueueTree` unchanged.
 *
 * Hidden names are filtered per path segment: the input does not prune the
 * way the drop walk does, so `Docs/._junk/a.txt` would otherwise land as a
 * real folder.
 *
 * Empty directories are absent by construction — a FileList of files cannot
 * express one, not even the picked root when it holds nothing.
 */
export function buildFolderUploadTree(
  entries: { file: File; relativePath: string }[],
): FolderUploadTree {
  const folders: string[] = [];
  const seen = new Set<string>();
  const files: { file: File; relativePath: string }[] = [];

  for (const entry of entries) {
    const segments = entry.relativePath.split("/").filter(Boolean);
    if (segments.length === 0) continue;
    if (segments.some(isHidden)) continue;

    let prefix = "";
    for (const segment of segments.slice(0, -1)) {
      prefix += `${segment}/`;
      if (seen.has(prefix)) continue;
      seen.add(prefix);
      folders.push(prefix);
    }
    files.push({ file: entry.file, relativePath: segments.join("/") });
  }

  return { folders, files, looseFiles: [] };
}

/**
 * The same fold for a `webkitdirectory` input's flat `FileList`, which
 * reports each file's path in `webkitRelativePath` (empty for a plain file
 * input, hence the `file.name` fallback).
 */
export function buildFolderUploadTreeFromFileList(
  files: FileList | File[],
): FolderUploadTree {
  return buildFolderUploadTree(
    Array.from(files).map((file) => ({
      file,
      relativePath:
        (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
        file.name,
    })),
  );
}

export async function traverseDataTransferItems(
  items: DataTransferItemLike[],
): Promise<FolderUploadTree> {
  const out: FolderUploadTree = { folders: [], files: [], looseFiles: [] };

  // Capture top-level entries synchronously up front. The items array
  // is already a snapshot per the caller contract; the entries returned
  // here remain valid for async traversal.
  const topLevel: FileSystemEntryLike[] = [];
  for (const item of items) {
    if (item.kind !== "file") continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) topLevel.push(entry);
  }

  for (const entry of topLevel) {
    if (isHidden(entry.name)) continue;
    if (entry.isFile) {
      try {
        const file = await entryAsFile(entry as FileSystemFileEntryLike);
        out.looseFiles.push(file);
      } catch {
        // ignored
      }
      continue;
    }
    if (entry.isDirectory) {
      await walkEntry(entry, "", out);
    }
  }

  return out;
}
